import { useEffect, useMemo } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useLots } from '../hooks/useLots.js'
import Filters from '../components/Filters.jsx'
import LotCard from '../components/LotCard.jsx'
import Loader from '../components/Loader.jsx'
import ErrorState from '../components/ErrorState.jsx'
import EmptyState from '../components/EmptyState.jsx'
import Pager from '../components/Pager.jsx'
import { SORT_OPTIONS } from '../data/constants.js'

// Página de búsqueda/listado. Los filtros viven en la URL (?q=&category=&...),
// así cada búsqueda es enlazable y funciona con volver/adelante del navegador.
export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Convertir query params en objeto de filtros
  const filters = useMemo(() => {
    const f = {}
    const map = {
      q: ['q', 'search'],
      category: ['category', 'cat'],
      country: ['country'],
      status: ['status'],
      order: ['order'],
      min_price: ['min_price'],
      max_price: ['max_price'],
      closes_before: ['closes_before'],
      closes_after: ['closes_after'],
      house: ['house'],
      with_price: ['with_price']
    }
    for (const [key, aliases] of Object.entries(map)) {
      for (const a of aliases) {
        const v = searchParams.get(a)
        if (v) { f[key] = v; break }
      }
    }
    return f
  }, [searchParams])

  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10))

  // Parámetros para la API. Estado por defecto: activo (requisito 3); los
  // terminados solo aparecen si el usuario elige explícitamente "Beendet".
  const apiParams = useMemo(() => {
    const ff = { ...filters }
    if (!ff.status) ff.status = 'active'
    return {
      ...ff,
      page,
      per_page: filters.per_page || 18,
      order: filters.order || 'closing'
    }
  }, [filters, page])

  const { data, pagination, loading, error, refetch } = useLots(apiParams)

  // Actualizar la URL cuando se aplican filtros
  const applyFilters = (next) => {
    const sp = new URLSearchParams()
    Object.entries(next).forEach(([k, v]) => {
      if (v !== undefined && v !== '' && v !== null) sp.set(k, v)
    })
    sp.delete('per_page')
    setSearchParams(sp, { replace: true })
  }

  useEffect(() => {
    document.title = 'Auktionssuche - ' + (filters.q ? `„${filters.q}“ ` : '') + '| Zuchlag'
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', 'https://zuchlag.de/suche')
  }, [filters.q])

  const activeFilterCount = Object.values(filters).filter((v) => v !== '' && v !== undefined).length
  const total = pagination?.total ?? 0

  return (
    <div className="page-search container">
      <div className="page-head">
        <h1>Auktionen durchsuchen</h1>
        <p>Gebrauchte Maschinen, Fahrzeuge und Anlagen aus Auktionen in ganz Europa.</p>
      </div>

      <Filters filters={filters} onChange={applyFilters} />

      {/* Resumen de resultados */}
      <div className="results-bar">
        {loading ? (
          <span>Suche…</span>
        ) : total > 0 ? (
          <span><strong>{total}</strong> Auktion{filters.q ? ` zu „${filters.q}“` : ''} gefunden</span>
        ) : (
          <span>Keine Ergebnisse</span>
        )}
        {activeFilterCount > 0 && (
          <Link to="/suche" className="results-clear">Filter zurücksetzen ✕</Link>
        )}
      </div>

      {loading && <Loader />}

      {error && <ErrorState message={error} onRetry={refetch} />}

      {!loading && !error && data.length === 0 && (
        <EmptyState
          title="Keine Auktionen gefunden"
          message="Versuchen Sie es mit anderen Suchbegriffen oder entfernen Sie Filter."
        >
          {filters.q && <Link to="/suche" className="btn btn-outline">Alle Auktionen anzeigen</Link>}
        </EmptyState>
      )}

      {!loading && !error && data.length > 0 && (
        <>
          <div className="lot-grid">
            {data.map((lot) => <LotCard key={lot.id} lot={lot} />)}
          </div>
          <Pager page={page} totalPages={pagination?.totalPages} />
        </>
      )}
    </div>
  )
}
